// File: src/pages/host/HostDashboard.jsx

import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import HostingProgressCard from '../../components/host/HostingProgressCard';
import EarningsSummary from '../../components/host/EarningsSummary';
import { LuCirclePlus } from "react-icons/lu";
import { LuSettings, LuClipboardList } from "react-icons/lu";
import HostAgendaPreviewModal from '../../components/host/modals/HostAgendaPreviewModal';

export default function HostDashboard() {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();


  // -----------------------------
  // REAL DATA (WIRED)
  // -----------------------------
  const [pendingEvents, setPendingEvents] = useState([]);
  const [approvedEventsThisMonth, setApprovedEventsThisMonth] = useState([]);
const [earnings, setEarnings] = useState(null);
const [payoutStatus, setPayoutStatus] = useState(null);
const [payoutLoading, setPayoutLoading] = useState(false);
const [promoter, setPromoter] = useState(null);
const [approvedEvents, setApprovedEvents] = useState([]);
const [payouts, setPayouts] = useState([]);
const [historyLoading, setHistoryLoading] = useState(true);
  
const [agendaOpen, setAgendaOpen] = useState(false);
const [agendaEvent, setAgendaEvent] = useState(null);

function openAgenda(ev) {
  setAgendaEvent(ev);
  setAgendaOpen(true);
}
function closeAgenda() {
  setAgendaOpen(false);
  setAgendaEvent(null);
}

// -----------------------------
// HOST DOCS GATE
// -----------------------------
const [docsChecked, setDocsChecked] = useState(false);
const [docsAccepted, setDocsAccepted] = useState(false);
const [docsGateOpen, setDocsGateOpen] = useState(false);
const [docsRequiredVersion, setDocsRequiredVersion] = useState('');

// -----------------------------
  // GATE NON-HOSTS
  // -----------------------------
  useEffect(() => {
    if (!currentUser?.isHost) {
      navigate('/events', { replace: true });
    }
  }, [currentUser, navigate]);


  // -----------------------------
// CHECK HOST DOCS ACCEPTANCE
// -----------------------------
useEffect(() => {
  if (!currentUser?.isHost) return;

  let mounted = true;

  async function checkDocs() {
    try {
      const res = await axios.get('/host/docs/status'); // axiosGlobal attaches user token

      if (!mounted) return;

      const isHost = !!res.data?.isHost;
      const accepted = !!res.data?.accepted;
      const requiredVersion = String(res.data?.requiredVersion || '');

      setDocsRequiredVersion(requiredVersion);
      setDocsAccepted(isHost && accepted);
if (isHost && !accepted) {
  navigate('/host/docs', { replace: true });
}
setDocsGateOpen(false);      setDocsChecked(true);
    } catch (err) {
      // If this fails, don't deadlock the user. Mark checked.
      if (!mounted) return;
      console.error('[HostDashboard] docs status check failed', err);
      setDocsChecked(true);
    }
  }

  checkDocs();

  return () => {
    mounted = false;
  };
}, [currentUser, navigate]);
  // -----------------------------
  // FETCH HOST DASHBOARD DATA
  // -----------------------------
useEffect(() => {
  if (!currentUser?.isHost) return;
  if (!docsChecked || !docsAccepted) return;

    async function fetchDashboard() {
      try {
        const token =
          localStorage.getItem('accessToken') ||
          localStorage.getItem('authToken') ||
          '';

        const res = await axios.get(
  `${process.env.REACT_APP_API_URL}/host/dashboard`,
  { headers: { Authorization: `Bearer ${token}` } }
);

console.log('[HostDashboard] API response:', res.data);


        const pending = res.data?.pending || [];
const approved = res.data?.approved || [];
const promoterData = res.data?.promoter || null;

setPendingEvents(pending);
setApprovedEvents(approved);
setPromoter(promoterData);

        // keep "this month" behavior exactly as UI implies
const now = new Date();

const approvedThisMonth = approved.filter((e) => {
  if (!e.eventDate) return false;
  const d = new Date(e.eventDate);
  return (
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear()
  );
});

// ✅ remove expired from "Upcoming"
const approvedThisMonthUpcoming = approvedThisMonth.filter((e) => !isExpired(e));

setPendingEvents(pending);
setApprovedEventsThisMonth(approvedThisMonthUpcoming);

      } catch (err) {
        console.error('[HostDashboard] dashboard fetch failed', err);
      }
    }

    fetchDashboard();
}, [currentUser, docsChecked, docsAccepted]);

  useEffect(() => {
  if (!currentUser?.isHost) return;
  if (!docsChecked || !docsAccepted) return;


  async function fetchPayoutHistory() {
    try {
      const token =
        localStorage.getItem('accessToken') ||
        localStorage.getItem('authToken') ||
        '';

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/host/payouts/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPayouts(res.data?.payouts || []);
    } catch (err) {
      console.error('[HostDashboard] payout history fetch failed', err);
    } finally {
      setHistoryLoading(false);
    }
  }

  fetchPayoutHistory();
}, [currentUser, docsChecked, docsAccepted]);


useEffect(() => {
  if (!currentUser?.isHost) return;
  if (!docsChecked || !docsAccepted) return;


  async function fetchPayoutData() {
    try {
      const token =
        localStorage.getItem('accessToken') ||
        localStorage.getItem('authToken') ||
        '';

      const [earningsRes, payoutRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API_URL}/host/earnings`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL}/host/payouts/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
      ]);

      setEarnings(earningsRes.data);
      setPayoutStatus(payoutRes.data);
    } catch (err) {
      console.error('[HostDashboard] payout fetch failed', err);
    }
  }

  fetchPayoutData();
}, [currentUser, docsChecked, docsAccepted]);

  if (!currentUser?.isHost) return null;


const stripeReady =
  !!payoutStatus?.stripeConnected &&
  !!payoutStatus?.stripeBankConnected;

  // -----------------------------
  // STATUS RENDER
  // -----------------------------
  function formatEventDate(dateStr) {
  if (!dateStr) return '';
const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function to12Hour(timeStr) {
  if (!timeStr) return '';

  // Already has AM/PM → trust it
  if (timeStr.includes('AM') || timeStr.includes('PM')) {
    return timeStr;
  }

  // Expect "HH:mm"
  const [h, m] = timeStr.split(':').map(Number);
  if (Number.isNaN(h)) return timeStr;

  const hour12 = ((h + 11) % 12) + 1;
  const ampm = h >= 12 ? 'PM' : 'AM';

  return `${hour12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
}

function parseTimeToMinutes(t) {
  if (!t) return null;
  const s = String(t).trim();

  // 24h "HH:MM"
  if (/^\d{2}:\d{2}$/.test(s)) {
    const [hh, mm] = s.split(':').map(Number);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
    return hh * 60 + mm;
  }

  // 12h "h:mm AM/PM"
  const m = s.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
  if (!m) return null;

  let hh = Number(m[1]);
  const mm = Number(m[2]);
  const ap = m[3].toUpperCase();

  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  if (hh < 1 || hh > 12 || mm < 0 || mm > 59) return null;

  hh = hh % 12; // 12 -> 0
  if (ap === 'PM') hh += 12;

  return hh * 60 + mm;
}

function getEventEndAtLocal(e) {
  if (!e?.eventDate) return null;

  const endMin = parseTimeToMinutes(e.endTime12 || e.endTime);
  if (endMin == null) return null;

  const startMin = parseTimeToMinutes(e.startTime12 || e.startTime);
  const isOvernight = startMin != null && endMin < startMin;

  const [y, mo, d] = String(e.eventDate).split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;

  const hh = Math.floor(endMin / 60);
  const mm = endMin % 60;

  return new Date(y, mo - 1, d + (isOvernight ? 1 : 0), hh, mm, 0, 0);
}

function isExpired(e) {
  const endAt = getEventEndAtLocal(e);
  if (!endAt) return true; // missing data => don't keep it in Upcoming
  return Date.now() > endAt.getTime();
}



function formatTimeRange(start, end) {
  const s = to12Hour(start);
  const e = to12Hour(end);

  if (!s) return '';
  if (!e) return s;

  return `${s} – ${e}`;
}

function countApprovedEventsLastMonth(events) {
  const now = new Date();

  const lastMonth = new Date(
    now.getFullYear(),
    now.getMonth() - 1,
    1
  );

  return events.filter((e) => {
    if (!e.eventDate) return false;

    const d = new Date(e.eventDate);

    return (
      d.getMonth() === lastMonth.getMonth() &&
      d.getFullYear() === lastMonth.getFullYear()
    );
  }).length;
}
  // -----------------------------
  // UI-ONLY PLACEHOLDER DATA
  // -----------------------------
let hostStatus = 'active';

// If promoter info not loaded yet, stay active (no flicker)
if (promoter?.hostSince) {
  const hostSince = new Date(promoter.hostSince);
  const now = new Date();

  const inGracePeriod =
    hostSince.getFullYear() === now.getFullYear() &&
    hostSince.getMonth() === now.getMonth();

  if (!inGracePeriod) {
    const lastMonthEventCount =
      countApprovedEventsLastMonth(approvedEvents);

    if (lastMonthEventCount === 0) {
      hostStatus = 'removed';
    } else if (lastMonthEventCount === 1) {
      hostStatus = 'warning';
    }
  }
}




  function renderStatus() {
    if (hostStatus === 'active') {
      return (
        <div style={ui.statusActive}>
          <strong>Hosting Status: Active</strong>
          <p>You’re on track this month.</p>
        </div>
      );
    }

    if (hostStatus === 'warning') {
      return (
        <div style={ui.statusWarning}>
          <strong>Hosting Status: Warning</strong>
          <p>
            You hosted fewer than 2 events last month.
            <br />
            Submit <strong>1–2 events soon</strong> to avoid losing host access.
          </p>
        </div>
      );
    }

    return (
      <div style={ui.statusRemoved}>
        <strong>Hosting Status: Inactive</strong>
        <p>Your host access has been removed due to inactivity.</p>
      </div>
    );
  }

  return (
    <div style={ui.page}>
      <div style={ui.container}>
        {/* HEADER */}
      



       {/* =============================
    EARNINGS
============================== */}
<EarningsSummary
  earnings={earnings}
  payouts={payouts}
  historyLoading={historyLoading}
/>



        <div style={ui.sectionDivider} />

   
     {/*
<button
  style={ui.linkBtn}
  onClick={() => navigate('/host/history')}
>
  View Past Events
</button>
*/}

        {/* =============================
    PENDING EVENTS
============================== */}
<div style={ui.section}>
  <h2 style={ui.sectionLabel}>Pending Review</h2>

  {pendingEvents.length === 0 ? (
    <p style={ui.emptyText}>No pending events.</p>
  ) : (
    pendingEvents.map((ev) => {
      let badgeStyle = ui.pendingBadge;
      let badgeLabel = 'Pending';
      let buttonLabel = 'Review';
      let buttonAction = () =>
        navigate(`/host/add/${ev._id}`);

      if (ev.status === 'changes_requested') {
        badgeStyle = ui.changesBadge;
        badgeLabel = 'Edits Needed';
        buttonLabel = 'Edit';
        buttonAction = () =>
          navigate(`/host/add/${ev._id}`);
      }

      if (ev.status === 'rejected') {
        badgeStyle = ui.rejectedBadge;
        badgeLabel = 'Rejected';
        buttonLabel = 'Review';
        buttonAction = () =>
          navigate(`/host/add/${ev._id}`);
      }

      const cover = ev.galleryImages?.[0];

      return (
        <div key={ev._id} style={ui.eventRow}>
          {/* IMAGE */}
          <div style={ui.eventImageWrap}>
            {cover ? (
              <img
                src={cover}
                alt={ev.name}
                style={ui.eventImage}
              />
            ) : (
              <div style={ui.eventImageFallback} />
            )}
          </div>

          {/* MAIN */}
          <div style={ui.eventMain}>
            <div style={ui.eventName}>{ev.name}</div>
            <div style={ui.eventMeta}>
              {formatEventDate(ev.eventDate)} ·{' '}
              {formatTimeRange(ev.startTime12, ev.endTime12)}
            </div>
          </div>

          {/* ACTIONS */}
<div style={ui.eventActions}>
  <span style={badgeStyle}>{badgeLabel}</span>
  <button
    style={ui.viewBtn}
    onClick={buttonAction}
  >
    {buttonLabel}
  </button>
</div>


        </div>
      );
    })
  )}
</div>

        {/* =============================
            APPROVED EVENTS
        ============================== */}
        <div style={ui.section}>
          <h2 style={ui.sectionLabel}>Upcoming Events</h2>
        {approvedEventsThisMonth.length === 0 ? (
  <p style={ui.emptyText}>No approved events yet.</p>
) : (
  approvedEventsThisMonth.map((ev) => {
    const eventId = ev.approvedEventId || ev._id;
    const cover = ev.galleryImages?.[0];

    return (
      <div key={eventId} style={ui.eventRow}>
        {/* IMAGE */}
        <div style={ui.eventImageWrap}>
          {cover ? (
            <img
              src={cover}
              alt={ev.name}
              style={ui.eventImage}
            />
          ) : (
            <div style={ui.eventImageFallback} />
          )}
        </div>

        {/* MAIN */}
        <div style={ui.eventMain}>
          <div style={ui.eventName}>{ev.name}</div>
        <div style={ui.eventMeta}>
  {formatEventDate(ev.eventDate)} ·{' '}
  {formatTimeRange(ev.startTime12, ev.endTime12)}
  {' '}· {Number(ev.ticketsSold ?? 0)} ticket
  {Number(ev.ticketsSold ?? 0) === 1 ? '' : 's'}
</div>

        </div>

    {/* ACTIONS */}
<div style={ui.eventActions}>
  <span style={ui.liveBadge}>Approved</span>

  <button
    style={ui.agendaBtn}
    onClick={() => openAgenda(ev)}
    title="View agenda"
  >
    <LuClipboardList size={12} />
  </button>

  <button
    style={ui.viewBtn}
    onClick={() => navigate(`/events/${eventId}`)}
  >
    View
  </button>
</div>

      </div>
    );
  })
)}


        </div>
        {/* =============================
    FIXED BOTTOM ACTION BAR
============================== */}
{hostStatus !== 'removed' && (
  <>
    {/* Footer background + safe area */}
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '72px',
        backgroundColor: '#fff',
        zIndex: 999,
        borderTop: '1px solid #eef0f2',
      }}
    />

    {/* Footer buttons (EXACT style as ProfileEditPage) */}
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        display: 'flex',
        gap: 10,
        padding: '12px 16px',
        backgroundColor: '#fff',
      }}
    >
      {!stripeReady ? (
        /* 🚫 Stripe not ready → ONLY payout settings */
      <button
  style={{
    flex: 1,
    border: '1px solid #000',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 800,
    padding: '12px 0',
    cursor: 'pointer',
    background: '#000',
    color: '#fff',

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  }}
  onClick={() => navigate('/host/payouts')}
>
  <LuSettings size={16} color="#fff" />
  Payout settings
</button>

      ) : (
        /* ✅ Stripe ready → ONLY add event */
     <button
  style={{
    flex: 1,
    border: '1px solid #000',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 800,
    padding: '12px 0',
    cursor: 'pointer',
    background: '#000',
    color: '#fff',

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  }}
  onClick={() => navigate('/host/add')}
>
  <LuCirclePlus size={16} color="#fff" />
  New Event
</button>

      )}
    </div>
  </>
)}
<HostAgendaPreviewModal
  open={agendaOpen}
  event={agendaEvent}
  onClose={closeAgenda}
/>

      </div>
    </div>
  );
}

/* ===============================
   CLEAN, SHARED UI STYLES
================================ */

const ui = {
  page: {
    background: '#fff',
    minHeight: '100vh',
  },
 container: {
  maxWidth: 760,
  margin: '0 auto',
  padding: '16px 16px 96px', // 👈 bottom padding for fixed bar
},

  pageTitle: {
    fontSize: 22,
    fontWeight: 900,
    marginBottom: 4,
  },
  pageSub: {
    fontSize: 14,
    color: '#555',
    marginBottom: 16,
  },

  section: {
    marginTop: 16,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#555',
    marginBottom: 8,
  },
  sectionDivider: {
    height: 1,
    backgroundColor: '#EEF0F2',
    margin: '16px 0',
  },

  statusActive: {
    padding: 12,
    background: '#EEF8EE',
    borderRadius: 8,
    marginBottom: 16,
  },
  statusWarning: {
    padding: 12,
    background: '#FFF6DD',
    borderRadius: 8,
    marginBottom: 16,
  },
  statusRemoved: {
    padding: 12,
    background: '#FDECEC',
    borderRadius: 8,
    marginBottom: 16,
  },

  earningsRow: {
    display: 'flex',
    alignItems: 'center',
  },
  earningsAmount: {
    fontSize: 28,
    fontWeight: 900,
  },
  metaText: {
    fontSize: 13,
    color: '#555',
    marginTop: 4,
  },
  warningText: {
    fontSize: 13,
    color: '#A15C00',
    marginTop: 4,
  },

  primaryBtn: {
    width: '100%',
    height: 44,
    borderRadius: 10,
    border: 'none',
    background: '#000',
    color: '#fff',
    fontSize: 12,
    fontWeight: 900,
    cursor: 'pointer',
    marginTop: 16,
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    padding: 0,
    marginTop: 12,
    color: '#000',
    fontWeight: 800,
    cursor: 'pointer',
  },

  emptyText: {
    fontSize: 13,
    color: '#777',
  },
  eventRow: {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '6px 6px',
  borderRadius: 12,
  background: '#fefefeff',
  border: '1px solid #E5E7EB',
  marginBottom: 10,
},

eventImageWrap: {
  width: 40,
  height: 40,
  flexShrink: 0,
  borderRadius: 8,
  overflow: 'hidden',
  background: '#E5E7EB',
},

eventImage: {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
},

eventImageFallback: {
  width: '100%',
  height: '100%',
  background: '#E5E7EB',
},

eventMain: {
  flex: 1,
  minWidth: 0,          // 🔑 REQUIRED for ellipsis in flexbox
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
},


eventName: {
  fontSize: 12,
  fontWeight: 600,
  color: '#111',

  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
},


eventMeta: {
  fontSize: 12,
  color: '#6B7280',
},

eventActions: {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
},

liveBadge: {
  fontSize: 11,
  fontWeight: 800,
  padding: '4px 10px',
  borderRadius: 999,
  background: '#E6F4EA',
  color: '#166534',
},

viewBtn: {
  border: 'none',
  background: '#000',
  color: '#fff',
  fontSize: 11,
  fontWeight: 900,
  padding: '8px 14px',
  borderRadius: 999,
  cursor: 'pointer',
},
pendingScroller: {
  display: 'flex',
  gap: 12,
  overflowX: 'auto',
  paddingBottom: 8,

  scrollbarWidth: 'none',      // Firefox
  msOverflowStyle: 'none',     // IE / Edge
},


pendingCard: {
  width: 220,          // 👈 narrower card
  maxWidth: 220,
  flexShrink: 0,

  padding: 8,
  borderRadius: 14,
  background: '#fefefe',
  border: '1px solid #E5E7EB',

  display: 'flex',
  flexDirection: 'column',
  gap: 8,
},


pendingTitle: {
  fontSize: 13,
  fontWeight: 900,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
},

pendingMeta: {
  fontSize: 12,
},

pendingBadge: {
  fontSize: 11,
  fontWeight: 800,
  padding: '4px 8px',
  borderRadius: 999,
  background: '#FEF3C7',
  color: '#92400E',
},

changesBadge: {
  fontSize: 11,
  fontWeight: 800,
  padding: '4px 8px',
  borderRadius: 999,
  background: '#DBEAFE',
  color: '#1E40AF',
},

rejectedBadge: {
  fontSize: 11,
  fontWeight: 800,
  padding: '4px 8px',
  borderRadius: 999,
  background: '#FEE2E2',
  color: '#991B1B',
},

pendingBtn: {
  width: '100%',
  height: 36,
  borderRadius: 999,
  border: 'none',
  background: '#000',
  color: '#fff',
  fontSize: 11,
  fontWeight: 900,
  cursor: 'pointer',
},
pendingSubMeta: {
  fontSize: 11,
  color: '#6B7280',
},

pendingDescription: {
  fontSize: 12,
  color: '#374151',
  lineHeight: 1.4,

  display: '-webkit-box',
  WebkitLineClamp: 2,      // 👈 1–2 lines max
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
},
pendingImages: {
  display: 'flex',
  gap: 6,
},

pendingImage: {
  width: '100%',
  height: 72,
  borderRadius: 10,
  objectFit: 'cover',
},
pendingImageScroller: {
  display: 'flex',
  gap: 8,
  overflowX: 'auto',
  scrollSnapType: 'x mandatory',

  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
},

pendingImageSlide: {
  flex: '0 0 65%',     // 👈 key change: narrower image tiles
  scrollSnapAlign: 'start',
},


pendingImage: {
  width: '100%',
  height: 100,         // 👈 slightly reduced height to match narrower card
  borderRadius: 10,
  objectFit: 'cover',
  display: 'block',
},
bottomActionBar: {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,

  display: 'flex',
  gap: 12,
  padding: 12,

  background: '#fff',
  borderTop: '1px solid #E5E7EB',

  zIndex: 1000,
},

primaryActionBtn: {
  flex: 1,
  height: 44,
  borderRadius: 12,
  border: 'none',
  background: '#000',
  color: '#fff',
  fontSize: 13,
  fontWeight: 900,
  cursor: 'pointer',
},

secondaryActionBtn: {
  flex: 1,
  height: 44,
  borderRadius: 12,
  border: '1px solid #000',
  background: '#fff',
  color: '#000',
  fontSize: 13,
  fontWeight: 900,
  cursor: 'pointer',
},
agendaBtn: {
  border: '1px solid #E5E7EB',
  background: '#fff',
  color: '#111',
  fontSize: 1,
  fontWeight: 900,
  padding: '8px 8px',
  borderRadius: 999,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0,
},



};
